
import { Option, ChargeTypeOption, SizeTypeOption, PropertyTypeOption, StateOrProvinceOption, PublishingSiteOption, DemoGraphicsOption, EnergyRatingOptions } from '../common/option';
import { SpecsConfig } from './specs/specs';
import { SpacesConfig } from './spaces/spaces';
import { GridConfig } from './common/grid';
import { FormDateFieldProps } from '../../components/form-date-field/form-date-field'

export interface ConfigDetails {
    loaded: boolean,
    forced: boolean,
    error: string,
    config: Config,
    homeSiteId?: string,
    intercomm?: boolean,
    intercommAppId?: string,
    intercommUserJwt?: string,
    aiKey: string,
    regions: Region[],
    propertyTypeColors: Map<string,string>,  /* a lookup for our property type colors */
    propertyTypeLabels: Map<string,string> /* a lookup for our property type labels */
}

export interface Region {
    countryCode: string
    cultureCode: string
    homeSiteID: string
    iD: string
    listingPrefix: string
    name: string
    previewPrefix: string
    previewSiteID: string
}

export interface Config {
    legalDisclosureText: string,
    intercomm?:boolean,
    intercommAppId?:string,
    supportLink?: boolean,   /* Link for submitting a support request to CBRE */
    supportMultiProperties?: boolean, /* Support for multiple properties in a single request */
    supportMultipleUseClass?: boolean, /* Support for multiple use classes in a single request */
    siteId: string,
    miqCountryCode: string,
    featureFlags: FeatureFlagsConfig,
    defaultMeasurement: string,
    highLightsMaxLength: number,
    addToTeam: Teams,
    map: MapConfig,
    coordinates: Coordinates,
    propertyType: PropertyTypeFieldConfig<PropertyTypeOption>,
    sortType: PropertyTypeFieldConfig<PropertyTypeOption>,
    listingTypes: FieldConfig<Option>,
    listingStatuses?: FieldConfig<Option>,
    numberOfListings: FieldConfig<Option>,
    brokerTypes: FieldConfig<Option>,
    officeLocation: OfficeLocationCofig<Option>,
    displayAddress:FieldConfig<Option>,
    propertySubType: FieldConfig<Option>,
    propertyUseClass: FieldConfig<Option>,
    disclaimer?: DisclaimerConfig,
    city: FieldSizeConfig,
    county: FieldSizeConfig,
    country: FieldConfig<Option>,
    stateOrProvince: StateFieldConfig,
    postalCode: FieldSizeConfig,
    investmentDescription: InvestmentDescriptionFieldConfig,
    highlight?: HighlightConfig,
    syndication?: SyndicationConfig,
    microMarkets: FieldConfig<Option>,
    specifications: SpecsConfig,
    spaces: SpacesConfig,
    contacts: ContactsConfig,
    validations: ValidationConfig,
    teamsEnabled: boolean,
    exportEnabled: boolean,
    duplicateEnabled: boolean,
    googleTrackingId?: string,
    aspects: AspectsConfig,
    chargesAndModifiers: ChargesConfig,
    parkings: ParkingsConfig,
    demographics: DemographicsConfig,
    auction : AuctionConfig,
    leasedInfo: LeasedInfoConfig,
    pointsOfInterests?: POIConfig,
    transportationTypes?: TTConfig,
    sizesandmeasurements: SizeTypeConfig,
    currencyCode: string,
    currencySymbol: string,
    watermark: boolean,
    floorPlansHelperText:string,
    publishingSites: PublishingSitesConfig<PublishingSiteOption>,
    watermarkDetectionFeatureFlag: boolean,
    displayMIQId?: boolean,
    displayAgentPointId?: boolean,
    insightsEnabled?: boolean,
    enableAuditHistory: boolean,
    enableBrochureURL?: boolean,
    publishedPropertyPrefix?:string,
    amenities: AmenitiesConfig,
    dataSource: any,
    floorsField?: FloorsField,
    yearField?: YearField,
    zoningField?: ZoningField,
    energyRating?: EnergyRatingConfig,
    epcRating?: EpcRatingConfig,
    tenancy?: EpcRatingConfig,
    dgnbRating?: DGNBRatingConfig,
    leedRating?: LeedRatingConfig,
    wellRating?: WellRatingConfig,
    nabersRating?: NABERSRatingConfig,
    berRating?: BerRatingConfig,
    status?: StatusConfig,
    availableFrom?: AvailableFromConfig,
    availabilityDetails?: AvailabilityDetailsConfig,
    availableImmediately?: AvailableImmediatelyConfig,
    languages?: string[],
    defaultCultureCode?: string,
    translations? : Translation[],
    headlineSingle? : HeadlineSingleConfig,
    epcGraphs? : EpcGraphsConfig,
    currencies: CurrencyConfig,
    report: ReportConfig,
    showExternalPropertyId: boolean,
    enableMultipleAvailabilityFloorPlans: boolean,
    streetAddress: string,
    exclusivity: StatusConfig,
    authority: StatusConfig,
    miqId: MiqIdField,
    salesOpportunity: SalesOpportunityField,
    portfolioPropertyType: FieldConfig<Option>,
    nabers: FieldConfig<Option>,
    majorEnergySources: FieldConfig<Option>,
    listingCardStatuses: ListingCardStatuses[];
    soldInfo: SoldInfoConfig,
    stateOfDelivery?: StateOfDeliveryConfig,
    fgee: FieldSizeConfig,
    classFgee: FieldConfig<EnergyRatingOptions>,
    hwb: FieldSizeConfig,
    classHwb: FieldConfig<EnergyRatingOptions>,
    streetAddressCheckbox?: StreetAddressCheckboxConfig,
    serviceAgreement?: ServiceAgreementConfig
}

export interface ListingCardStatuses {
    type: string,
    value: string,
    txtColor: string,
    bgColor: string,
}

export interface Translation {
    cultureCode: string,
    languageName: string,
    plHeadline: string,
    plBuildingDescription: string,
    plLocationDescription: string,
    plDisclaimer?: string

}
export interface Property {
    propertyType: string,
    listingType: string,
    teamName: string
}
export interface StreetAddressCheckboxConfig {
    show: boolean;
}

export interface Teams {
    properties: Property[];
}
export interface FeatureFlagsConfig {
    leaseRateTypeEnabled: boolean,
    miqSyncEnabled?: boolean,
    defaultWaterMarkTrue?: boolean,
    overrides?: Overrides,
    unpublishFromMIQExport?: boolean,
    autoCalculateSizeAndPrice?: boolean,
    hideStateOrProvince?: boolean,
    removeStateOrProvinceWhenSameAsCity?: boolean,
    hideMiqImportFeatureFlag?: boolean,
    enablePriceOnContactBroker: boolean,
    hideBulkUploadFeatureFlag: boolean,
    hidePlansFeatureFlag: boolean, 
    searchBrokerFeatureFlag?: boolean,
    enableBrokerTypeSelection?: boolean,
    showRemainingListingTypeInStatus?: boolean,
    miqSizesConversionRules?: MiqSizesConversionRules[],
    stateOfDeliveryRenderingRules?: StateOfDeliveryRenderingRulesConfig,
    skipSpecificationsResetOnListingType?: boolean,
    listingTypeForMiqSpecificationsMinPrice?: string[],
    listingTypeForMiqSpecificationsMinSpace?: string[],
    nonZeroMinPriceForMiqSpecificationsMinPrice: boolean,
    specificationsRules: FieldsByConditions[],
    showAvailabilityDetails?: boolean,
    showMinMaxPriceRange?: boolean,
    showDisclaimer?: boolean,
    showPublishedListingsCounts?: boolean,
    adminThirdPartyPublishingEnabled?: boolean,
    adminBrokerWorkflowEnabled?: boolean,
    // When true, applying selected MIQ sync updates also publishes the listing in the same action.
    miqApplyAndAutoPublish?: boolean
}

export interface Overrides{
    uatImportFromMIQRequireAdmin?: boolean
}

export interface FloorsField {
    show: boolean
}

export interface YearField {
    show: boolean
}

export interface ZoningField {
    show: boolean
}
export interface MiqIdField {
    show: boolean
}
export interface DisclaimerConfig {
    show: boolean
}
export interface SalesOpportunityField {
    show: boolean
}

export interface Coordinates {
    lat: number,
    lng: number
}

export interface MapConfig {
    component: string,
    places: PlacesConfig
    addressComponents?: AddressComponentsConfig
}

export interface SyndicationConfig {
    show: boolean,
    label?: string
    markets?: SyndicationMarketConfig[]
}

export interface SyndicationMarketConfig extends FieldConfig<Option> {
    propertyType: string
    grid: GridConfig
}

export interface HighlightConfig {
    label: string,
    addButtonLabel: string,
    helpText?: string,
    required: boolean
}

export interface PlacesConfig {
    api: string,
    limitSearch: boolean,
    limitCountryCodes: string[]
}

export interface AddressComponentsConfig {
    cityFieldName?: string
}

export interface ContactsConfig {
    phoneMask: string,
    title?: FieldConfig<Option>,
    homeOffice: FieldConfig<Option>,
    emailPlaceholder: string,
    emailSubText: string
}

export enum ConfigFieldType {
    FORM_INPUT = "FORM_INPUT",
    FORM_TEXT_AREA = "FORM_TEXT_AREA",
    FORM_TABBED_TEXT_AREA = "FORM_TABBED_TEXT_AREA",
    FORM_SELECT = "FORM_SELECT",
    FORM_DATE_FIELD = "FORM_DATE_FIELD",
    FORM_CHECKBOX = "FORM_CHECKBOX",
    CONDITIONAL_INCLUDE = "CONDITIONAL_INCLUDE",
    FORM_SPACE_DESC = "FORM_SPACE_DESC",
    FORM_SPACE_NAME = "FORM_SPACE_NAME",
}

export interface SizeTypeConfig {
    show: boolean,
    header: string,
    use?: string,
    label?: string,
    required?: boolean,
    sizeType?: SizeTypePropertyTypeOptionConfig[],
    unitofmeasure?: Option[],
    size?: number,
    helpText?: string
}

export interface ChargesConfig {
    show: boolean,
    label?: string,
    required: boolean,
    chargesType: ChargeTypeOption[],
    chargeModifier: Option[],
    term: Option[],
    perUnit: Option[],
    enablePerUnit: boolean,
    enableYear: boolean
}

export interface ParkingsConfig {
    show: boolean,
    label?: string,
    required: boolean,
    ratioPerUnit: Option[],
    parkingType: Option[],
    interval: Option[]
}

export interface DemographicsConfig {
    show: boolean,
    label?: string,
    unitOfMeasure: UnitOfMeasureConfig,
    categoryOptions: DemoGraphicsOption[]
}

export interface AuctionConfig {
    show: boolean,
    label?: string,
    required: boolean,
    hours: Option[],
    minutes: Option[],
    EventPlace: string,
    EventDate: AvailableFromConfig,
    EventTime: string
}
export interface LeasedInfoConfig {
    show: boolean,
    header: string,
    required: boolean,
    rentPerAnnum: FieldConfig<Option>,
    leasedPrice: FieldConfig<Option>,
    leasedDuration: FieldConfig<Option>,
    leasedDate: AvailableFromConfig,
    displayPrice: FieldConfig<Option>
}

export interface SoldInfoConfig  {
    show: boolean,
    header: string,
    required: boolean,
    soldPrice: FieldConfig<Option>,
    soldDate:AvailableFromConfig,
    displayPrice: FieldConfig<Option>,
  
}

export interface POIConfig {
    show: boolean,
    label?: string,
    required: boolean,
    interestKinds: string[],
    travelMode: Option[],
    distanceUnits: Option[]
}

export interface TTConfig {
    show: boolean, 
    label?: string,
    required: boolean, 
    types: string[],
    travelMode: Option[],
    distanceUnits: Option[]
}

export interface FieldConfig<T extends Option> {
    show: boolean,
    label: string,
    required: boolean,
    size?: string,
    options: T[],
    fieldType?: ConfigFieldType,
    optionVisibility?: Number[]
}

export interface PropertyTypeFieldConfig<T extends Option> extends DropdownFieldConfig<T> {
    clearSpacesOn: string[] // the values [from the options] where we need to clear UI fields
}

export interface DropdownFieldConfig<T extends Option> extends FieldConfig<T> {
    alphabeticalSort?: boolean
}

export interface OfficeLocationCofig<T extends Option>{
    show: boolean,
    label: string,
    required: boolean,
    size?: string,
    prodOptions: T[],
    nonProdOptions:T[],
    fieldType?: ConfigFieldType
}

export interface StateFieldConfig {
    show: boolean,
    label: string,
    required: boolean,
    defaultCountry?: string,
    size?: string,
    countryStates: StateOrProvinceOption[]
}

export interface FieldSizeConfig {
    show: boolean,
    size: string,
    label?: string
}

export interface ValidationConfig {
    unpublished: object,
    published: object,
    contactForm: object,
    manualValidations: object
}

export interface AspectsConfig {
    show: boolean,
    label: string,
    required: boolean,
    options: AspectConfig[]
}

export interface HeadlineSingleConfig {
    show: boolean,
    label?: string,
    required: boolean
}

export interface AspectConfig {
    id: string,
    display: string,
    default: boolean
}
export interface AvailableFromConfig extends FormDateFieldProps {
    show: boolean,
    grid: GridConfig
}

export interface AvailabilityDetailsConfig {
    show: boolean,
    label: string,
    children: AvailabilityChildConfig[],
    grid: GridConfig
}

export interface AvailableImmediatelyConfig {
    name: string,
    type: ConfigFieldType,
    label: string,
    show: boolean,
    required: boolean,
    order: number,
    grid: GridConfig
}

export interface AvailabilityChildConfig {
    name: string,
    type: ConfigFieldType,
    label: string,
    show: boolean,
    required: boolean,
    prompt: string,
    options?: Option[],
    yearRange?: {
        back: number,
        next: number
    },
    defaultValue?: string,
    order: number
}

export interface UnitOfMeasureConfig {
    defaultValue: string,
    options: Option[]

}
export interface EnergyRatingConfig extends FieldConfig<Option> {
    prompt: string,
    grid: GridConfig,
    tooltip: string
}

export interface LeedRatingConfig extends FieldConfig<Option> {
    prompt: string,
    grid: GridConfig
}

export interface WellRatingConfig extends FieldConfig<Option> {
    prompt: string,
    grid: GridConfig
}

export interface EpcRatingConfig extends FieldConfig<Option> {
    prompt: string,
    grid: GridConfig
}

export interface DGNBRatingConfig extends FieldConfig<Option> {
    prompt: string,
    grid: GridConfig
}

export interface NABERSRatingConfig extends FieldConfig<Option> {
    prompt: string,
    grid: GridConfig
}
export interface BerRatingConfig extends FieldConfig<Option> {
    prompt: string,
    grid: GridConfig
}

export interface EpcGraphsConfig {
    show: boolean,
    title: string,
    label: string,
    accepted: string,
    description: string,
    showPrimary: boolean,
    allowWatermarkingDetect: boolean
}

export interface CurrencyConfig {
    options: Option[],
    defaultValue: string
}

export interface ReportConfig{
    downloadText: string,
    exportFileName: string,
    loadingText: string
}

export interface InvestmentDescriptionFieldConfig{
    showPropertyTypes: string[]
}

export interface SizeTypePropertyTypeOptionConfig {
    PropertyTypes: string[]
    options: SizeTypeOption[]
}
export interface StateOfDeliveryRenderingRulesConfig {
    PropertyTypes: string[],
    ListingTypes: string[]
}
export interface PublishingSitesConfig<T extends PublishingSiteOption> {
    label: string,
    options: T[]
}
export interface StateOfDeliveryConfig {
    show: boolean,
    label: string,
    required: boolean,
    options: Option[],
    prompt: string,
    grid: GridConfig,
    defaultValue: string  
}

export interface StatusConfig extends FieldConfig<Option> {
    prompt: string,
    grid: GridConfig,
    defaultValue: string
}


export interface MiqSizesConversionRules {
    unit: string;
    conditions: {
        [key: string]: any;
    };
    spaces?: string[];
}

export interface FieldsByConditions {
    conditions: {
        [key: string]: any;
    };
    target: {
        [key: string]: any;
    };
}

export interface AmenitiesConfig {
    show: boolean,
    useMiqApi?: boolean,
    defaultOptions?: Array<{ key: string; value: string }>
}

export interface ServiceAgreementConfig {
    show: boolean,
    properties: {
        name: string,
        label: string
    },
    grid?: GridConfig
}